import Keycloak from 'keycloak-js';
import { coreEnv } from '../env';
import { setSessionToken } from './token-ref';
import type { Session } from './types';

/**
 * Keycloak-адаптер для authMode=oidc (FRONTEND_GUIDE §3.1): Authorization Code + PKCE,
 * публичный клиент. Session-модель (session/model.ts) вызывает init/login/logout,
 * а рефреш токена живёт здесь и обновляет token-ref напрямую (api-клиент читает его
 * на каждый запрос, устаревший $session.token никому не мешает).
 */

/** За сколько секунд до истечения access-токена обновлять его */
const REFRESH_MIN_VALIDITY_S = 60;
const REFRESH_CHECK_INTERVAL_MS = 20_000;

let keycloak: Keycloak | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

function getKeycloak(): Keycloak {
  if (keycloak === null) {
    keycloak = new Keycloak({
      url: coreEnv.oidcUrl,
      realm: coreEnv.oidcRealm,
      clientId: coreEnv.oidcClientId,
    });
  }
  return keycloak;
}

function sessionFromToken(kc: Keycloak): Session {
  const claims = (kc.tokenParsed ?? {}) as Record<string, unknown>;
  const email = typeof claims['email'] === 'string' ? (claims['email'] as string) : '';
  const preferred =
    typeof claims['preferred_username'] === 'string' ? (claims['preferred_username'] as string) : '';
  const name = typeof claims['name'] === 'string' ? (claims['name'] as string) : '';
  return {
    userId: kc.subject ?? '',
    email: email || preferred,
    name: name || preferred || email,
    token: kc.token ?? '',
    // Ролей у бэка пока нет — как и в мок-логине, даём все права
    permissions: ['*'],
  };
}

function startRefreshLoop(kc: Keycloak): void {
  if (refreshTimer !== null) return;
  refreshTimer = setInterval(() => {
    kc.updateToken(REFRESH_MIN_VALIDITY_S)
      .then((refreshed) => {
        if (refreshed) setSessionToken(kc.token ?? null);
      })
      .catch(() => {
        // refresh-токен истёк/отозван — единственный выход перелогиниться
        void kc.login();
      });
  }, REFRESH_CHECK_INTERVAL_MS);
}

/**
 * Инициализация на старте приложения: тихо проверяет SSO-сессию (iframe на
 * silent-check-sso.html из public/). Возвращает Session, если пользователь уже
 * залогинен в Keycloak, иначе null (shell покажет страницу входа).
 */
export async function initKeycloakSession(): Promise<Session | null> {
  const kc = getKeycloak();
  const authenticated = await kc.init({
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
    pkceMethod: 'S256',
  });
  if (!authenticated) return null;
  setSessionToken(kc.token ?? null);
  startRefreshLoop(kc);
  return sessionFromToken(kc);
}

/** Полный редирект на страницу входа Keycloak (вернёт на текущий URL) */
export function keycloakLogin(): Promise<void> {
  return getKeycloak().login({ redirectUri: window.location.href });
}

/** Выход из Keycloak-сессии (редирект; страница будет перезагружена) */
export function keycloakLogout(): Promise<void> {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  return getKeycloak().logout({ redirectUri: window.location.origin });
}
